import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { isElectron } from "../env";
import { useDesktopUpdateState } from "../state/desktopUpdate";
import { updateDesktopNow } from "./desktopUpdate.action";
import {
  DESKTOP_UPDATE_AUTOMATICALLY_TARGET_ID,
  desktopUpdateNotice,
  desktopUpdateNoticeKey,
  type DesktopUpdateNotice,
} from "./desktopUpdate.logic";
import { desktopUpdateNoticeToast } from "./desktopUpdate.toast";
import { toastManager } from "./ui/toast";

/** The versions whose notice has already popped up by itself, newest last. */
const POPPED_VERSIONS_KEY = "cleo:desktop-update:popped-versions";
const POPPED_VERSIONS_KEPT = 20;

function readPoppedVersions(): string[] {
  try {
    const raw: unknown = JSON.parse(window.localStorage.getItem(POPPED_VERSIONS_KEY) ?? "[]");
    return Array.isArray(raw) ? raw.filter((row): row is string => typeof row === "string") : [];
  } catch {
    // Per-viewer convenience only: unreadable storage pops up as if new.
    return [];
  }
}

function rememberPoppedVersion(version: string): void {
  try {
    const kept = readPoppedVersions().filter((row) => row !== version);
    window.localStorage.setItem(
      POPPED_VERSIONS_KEY,
      JSON.stringify([...kept, version].slice(-POPPED_VERSIONS_KEPT)),
    );
  } catch {
    // Storage refusal never breaks the notice; the session still remembers.
  }
}

/**
 * Says a found update in words and follows it to the end in one toast: the
 * version with Update and Settings, then the download running, then the wait
 * for Cleo's running turns, then the install. It lives in the Desk's one
 * layout, so it is mounted on every page. Until 20 September 2026 the only
 * update control outside Settings sat in the footer of a sidebar removed from
 * the screen on 28 August; until 23 September the notice kept offering
 * Download after it was pressed and ended by pointing at Settings (REL3).
 *
 * Can 30: it pops up by itself at most once per version, remembered across
 * restarts. Once closed, the Update button left of the bell is the reminder.
 */
function Announcer() {
  const state = useDesktopUpdateState();
  const navigate = useNavigate();
  const shown = useRef<{ readonly id: string; readonly key: string } | null>(null);
  const closedKey = useRef<string | null>(null);
  const popped = useRef<Set<string> | null>(null);
  const installing = useRef(false);

  useEffect(() => {
    const notice = desktopUpdateNotice(state ?? null);
    if (notice === null) {
      // A later check found nothing newer (the pointer moved back): nothing is left to say.
      if (state?.status === "up-to-date" && shown.current) {
        const { id } = shown.current;
        shown.current = null;
        toastManager.close(id);
      }
      return;
    }
    const found: DesktopUpdateNotice = notice;
    if (desktopUpdateNoticeKey(found) === closedKey.current) return;
    // Once per version: a notice already on screen follows its phases, and
    // a version that has popped up once waits by the bell instead.
    popped.current ??= new Set(readPoppedVersions());
    if (!shown.current && popped.current.has(found.version)) return;

    const openSettings =
      state?.automatic === undefined
        ? undefined
        : () => {
            void navigate({
              to: "/settings/general",
              hash: DESKTOP_UPDATE_AUTOMATICALLY_TARGET_ID,
              hashScrollIntoView: false,
            });
          };

    const show = (next: DesktopUpdateNotice) => {
      const options = desktopUpdateNoticeToast(next, { update, openSettings });
      const nextKey = desktopUpdateNoticeKey(next);
      // Every version shown counts as popped, also one a notice on screen
      // moved on to (a newer release replacing the download).
      popped.current?.add(next.version);
      rememberPoppedVersion(next.version);
      if (shown.current) {
        toastManager.update(shown.current.id, options);
        shown.current = { id: shown.current.id, key: nextKey };
        return;
      }
      const id = toastManager.add({
        ...options,
        onClose: () => {
          if (shown.current?.id !== id) return;
          closedKey.current = shown.current.key;
          shown.current = null;
        },
      });
      shown.current = { id, key: nextKey };
    };

    function update() {
      const bridge = window.desktopBridge;
      if (!bridge || installing.current) return;
      const step =
        found.kind === "downloaded" || found.kind === "install-failed" ? "install" : "download";
      const failed = (message: string | null) =>
        show(
          step === "download"
            ? { kind: "download-failed", version: found.version, message }
            : { kind: "install-failed", version: found.version, message },
        );
      // Said at once, before the Desk's next state arrives. An older Desk's
      // install asks its confirmation first and is said by the state after.
      if (step === "download") show({ kind: "downloading", version: found.version, percent: null });
      else if (bridge.updateNow) show({ kind: "installing", version: found.version });
      installing.current = step === "install";
      // A failure the Desk handles comes back in its state; only a refused call is said here.
      void updateDesktopNow(step, found.version)
        .catch((error: unknown) => {
          failed(error instanceof Error ? error.message : null);
        })
        .finally(() => {
          installing.current = false;
        });
    }

    show(found);
  }, [navigate, state]);

  return null;
}

export function DesktopUpdateAnnouncer() {
  return isElectron ? <Announcer /> : null;
}
